/**
 * Play counts — what players are feeding coins into.
 *
 * Two ledgers:
 *  - GLOBAL (preferred): GET /v1/plays aggregate across every cabinet,
 *    fetched at boot, cached in localStorage so the next boot orders
 *    correctly even before (or without) the network. Bumped
 *    optimistically in-session on each launch.
 *  - LOCAL (fallback): this device's own INSERT COIN tally, used when
 *    the API has never been reachable from this cabinet.
 *
 * Every launch also fires POST /v1/plays (fire-and-forget) so this
 * cabinet's coins count toward everyone else's ordering.
 */

import { getGlobalPlays, recordPlayRemote } from '../api/client.js';
import { REMASTER_VERSION } from '../catalog.js';
import { getOrCreateDeviceId } from '../runtime/identity.js';

const LOCAL_KEY = 'arcadia-remastered-plays';
const GLOBAL_CACHE_KEY = 'arcadia-remastered-global-plays';

function readMap(key: string): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Record<string, number>;
  } catch {
    return null;
  }
}

function writeMap(key: string, map: Record<string, number>): void {
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* private-mode Safari — ordering just stays session-static */
  }
}

let globalCounts: Record<string, number> | null = readMap(GLOBAL_CACHE_KEY);

/** The count the gallery should rank by: global if we've ever had it. */
export function playCountFor(gameId: string): number {
  if (globalCounts) {
    const n = globalCounts[gameId];
    return typeof n === 'number' && Number.isFinite(n) ? n : 0;
  }
  const local = readMap(LOCAL_KEY);
  const n = local?.[gameId];
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/**
 * Record one INSERT COIN: local ledger always, optimistic bump of the
 * in-memory global counts, and a fire-and-forget POST so the play
 * lands in everyone's aggregate.
 */
export function recordPlay(gameId: string): void {
  const local = readMap(LOCAL_KEY) ?? {};
  local[gameId] = (typeof local[gameId] === 'number' ? local[gameId] : 0) + 1;
  writeMap(LOCAL_KEY, local);

  if (globalCounts) {
    globalCounts[gameId] = playCountFor(gameId) + 1;
    writeMap(GLOBAL_CACHE_KEY, globalCounts);
  }

  void recordPlayRemote({
    cartridgeId: gameId,
    cartridgeVersion: REMASTER_VERSION,
    deviceId: getOrCreateDeviceId(),
  }).catch(() => {
    /* offline cabinet — the local ledger already has it */
  });
}

/**
 * Fetch fresh global counts. Returns true when counts arrived (caller
 * re-renders the gallery); false leaves whatever ledger we had.
 */
export async function refreshGlobalPlays(): Promise<boolean> {
  try {
    const fresh = await getGlobalPlays(REMASTER_VERSION);
    globalCounts = fresh.counts;
    writeMap(GLOBAL_CACHE_KEY, globalCounts);
    return true;
  } catch {
    return false;
  }
}
